/**
 * ContinueWritingCard — hero card on the dashboard for the most recently
 * edited story. This is the fastest path back into an active draft.
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import { fadeUp } from '@/lib/motion';
import { Magnetic } from '@/components/site/primitives';
import StoryCover from './StoryCover';
import { getStoryWordCount, getLastEditedChapter, formatRelativeTime } from './utils/storyHelpers';

export default function ContinueWritingCard({ story }) {
  const navigate = useNavigate();
  const wordCount = getStoryWordCount(story);
  const chapter = getLastEditedChapter(story);
  const chapterIndex = story.chapters?.findIndex((c) => c.id === chapter?.id) ?? -1;

  const goal = story.goal;
  const progressPct = goal ? Math.min(100, Math.round((wordCount / goal) * 100)) : null;

  const defaultChapterLabel = `Chapter ${chapterIndex + 1}`;
  const chapterLabel = chapter
    ? (chapter.title && chapter.title !== defaultChapterLabel
        ? `${defaultChapterLabel} · ${chapter.title}`
        : defaultChapterLabel)
    : 'No chapters yet';

  return (
    <motion.div
      variants={fadeUp}
      initial="hidden"
      animate="show"
      className="relative overflow-hidden rounded-2xl border border-line bg-card shadow-card"
      data-testid="my-writing-continue-card"
    >
      <div className="absolute -right-10 -top-16 h-52 w-52 rounded-full bg-[radial-gradient(circle,rgba(240,199,127,0.28),transparent_65%)]" aria-hidden="true" />
      <div className="relative flex flex-col gap-5 p-5 sm:flex-row sm:items-center sm:p-7">
        <StoryCover story={story} className="h-32 w-24 shrink-0 shadow-md sm:h-40 sm:w-28" />

        <div className="min-w-0 flex-1">
          <p className="text-xs font-semibold uppercase tracking-wide text-coral">Continue Writing</p>
          <h3 className="mt-1.5 truncate font-display text-2xl font-semibold text-ink">{story.title}</h3>
          <p className="mt-1 text-sm text-subtle">
            {chapterLabel} · {wordCount.toLocaleString()} words
          </p>
          <p className="mt-0.5 text-xs text-subtle">Last edited {formatRelativeTime(story.updatedAt)}</p>

          {goal && (
            <div className="mt-3 max-w-xs">
              <div className="mb-1 flex items-center justify-between text-[11px] font-medium text-subtle">
                <span>{wordCount.toLocaleString()} / {goal.toLocaleString()} words</span>
                <span className="text-coral">{progressPct}%</span>
              </div>
              <div className="h-1.5 w-full overflow-hidden rounded-full bg-line/70">
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: `${progressPct}%` }}
                  transition={{ duration: 1, ease: [0.22, 1, 0.36, 1] }}
                  className="h-full rounded-full bg-gradient-to-r from-coral to-yellow"
                />
              </div>
            </div>
          )}
        </div>

        <Magnetic className="shrink-0">
          <button
            type="button"
            onClick={() => navigate(`/app/studio/${story.id}`)}
            data-testid="my-writing-continue-cta"
            className="group inline-flex items-center gap-2 rounded-full bg-coral-fill px-5 py-3 text-sm font-semibold text-[#FFF7F0] shadow-soft transition-all duration-300 ease-soft hover:-translate-y-0.5 hover:shadow-lift"
          >
            Continue Writing
            <ArrowRight className="h-4 w-4 transition-transform duration-300 ease-soft group-hover:translate-x-1" />
          </button>
        </Magnetic>
      </div>
    </motion.div>
  );
}
